import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Emblem } from '../../components/Emblem';
import { getOrder, type OrderSummary } from '../../api/orders';
import { formatGroszeAsZl } from '../../util/money';
import { useShop } from '../ShopState';
import { OrderLookupForm } from '../OrderLookupForm';
import { ProductSpec } from '../ProductSpec';
import { StripePayButton } from '../StripePayButton';
import { SETTLEMENT_COPY, isPaymentConfirmed, usePaymentSettlement } from '../paymentSettlement';
import { COLOR_META } from '../colors';

export function Success() {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState<OrderSummary | null>(null);
  const [denied, setDenied] = useState(false);
  const { catalog, lastOrder, resetOrderSession } = useShop();
  const location = useLocation();
  const placedOrderId = (location.state as { placedOrderId?: string } | null)?.placedOrderId;
  // The link carries the token; fall back to the one kept in memory from checkout.
  const token = searchParams.get('t') ?? (lastOrder && lastOrder.id === orderId ? lastOrder.token : null);
  // Stripe's return trip cannot carry router state, so the payment outcome arrives as a query
  // marker. Read once: the marker is stripped from the URL below, and a reload of the cleaned
  // link must not resurrect it.
  const [paymentReturn] = useState(() => searchParams.get('platnosc'));

  useEffect(() => {
    if (!orderId || !token) return;
    let stop = false;
    getOrder(orderId, token)
      .then((o) => !stop && setOrder(o))
      .catch(() => !stop && setDenied(true));
    return () => {
      stop = true;
    };
  }, [orderId, token]);

  // Only the order just placed ends the session, plus the trip back from Stripe — by then the
  // order exists and the basket that built it must not survive into a second one. Reaching this
  // page any other way — the confirmation email, the surname lookup — is someone reading an
  // order, and must not touch a checkout in progress. The marker is consumed immediately:
  // history entries keep their state, so Back could otherwise replay this on a later basket.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const backFromStripe = params.has('platnosc');
    if (!orderId || (placedOrderId !== orderId && !backFromStripe)) return;
    resetOrderSession();
    params.delete('platnosc');
    const search = params.toString();
    navigate(`${location.pathname}${search ? `?${search}` : ''}${location.hash}`, {
      replace: true,
      state: null,
    });
  }, [
    orderId,
    placedOrderId,
    resetOrderSession,
    navigate,
    location.pathname,
    location.search,
    location.hash,
  ]);

  const isPaid = isPaymentConfirmed(order);
  const settlement = usePaymentSettlement({
    orderId,
    token,
    order,
    onOrder: setOrder,
    returnedFromPayment: paymentReturn === 'ok',
  });
  const productById = new Map(
    catalog.status === 'ready' ? catalog.products.map((p) => [p.id, p]) : [],
  );

  // No usable token, or the server rejected it: offer the surname lookup instead
  // of a blank page.
  if (!token || denied) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
        }}
      >
        <div style={{ maxWidth: 520, width: '100%', textAlign: 'center' }}>
          <Emblem size={140} style={{ margin: '0 auto' }} />
          <h1 className="display" style={{ fontSize: 30, marginTop: 18, color: 'var(--orange)' }}>
            POTWIERDŹ SWOJE ZAMÓWIENIE
          </h1>
          <p style={{ marginTop: 12, fontSize: 14, color: 'rgba(var(--shop-muted-rgb),.75)' }}>
            Ten link nie zawiera ważnego dostępu do zamówienia. Podaj numer i nazwisko.
          </p>
          <OrderLookupForm />
        </div>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', minHeight: '100vh', display: 'grid', gridTemplateColumns: '55% 45%' }}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 64,
          borderRight: '2px solid var(--shop-fg)',
          gap: 28,
        }}
      >
        <Emblem size={220} />
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 12,
            padding: '12px 24px',
            border: '3px solid var(--orange)',
            background: 'rgba(255,107,26,.08)',
            color: 'var(--orange)',
            fontFamily: 'var(--display)',
            fontSize: 26,
            letterSpacing: '.22em',
            transform: 'rotate(-3deg)',
            boxShadow: '0 0 0 5px var(--shop-bg), 0 0 0 7px rgba(255,107,26,.35)',
          }}
        >
          <span
            style={{ width: 14, height: 14, borderRadius: 999, background: 'var(--orange)', display: 'inline-block' }}
          />
          {isPaid ? 'ZAPŁACONE' : settlement ? 'PŁATNOŚĆ W TOKU' : 'CZEKA NA PŁATNOŚĆ'}
        </div>
        <div className="display" style={{ fontSize: 96, lineHeight: 0.92, color: 'var(--orange)' }}>
          {isPaid ? 'DZIĘKUJEMY' : settlement ? 'SPRAWDZAMY PŁATNOŚĆ' : 'DOKOŃCZ PŁATNOŚĆ'}
        </div>
        <div
          style={{
            fontSize: 16,
            color: 'rgba(var(--shop-muted-rgb),.75)',
            lineHeight: 1.5,
            textAlign: 'center',
            maxWidth: 460,
          }}
        >
          {isPaid ? (
            <>
              Gdyby nie było Was nie byłoby nas.
              <br />
              Widzimy się 28.11 na Teneryfie.
            </>
          ) : settlement ? (
            <>
              Wróciliśmy z płatności.
              <br />
              Sprawdzamy potwierdzenie u operatora płatności.
            </>
          ) : (
            <>
              Zamówienie jest zapisane, ale jeszcze nieopłacone.
              <br />
              Dokończ płatność, żebyśmy mogli je przyjąć.
            </>
          )}
        </div>
      </div>

      <div style={{ padding: '64px 56px', display: 'flex', flexDirection: 'column', gap: 22 }}>
        <div>
          <div className="mono" style={{ fontSize: 11, letterSpacing: '.16em', color: 'var(--orange)' }}>
            NUMER ZAMÓWIENIA
          </div>
          <div className="display" style={{ fontSize: 56, marginTop: 6, lineHeight: 1 }}>
            {orderId}
          </div>
        </div>

        {order && order.status === 'awaiting_payment' && (
          <>
            {(settlement || paymentReturn === 'anulowana') && (
              <div
                className="mono"
                style={{
                  padding: '12px 14px',
                  border: '1px dashed var(--orange)',
                  color: 'var(--orange)',
                  fontSize: 12,
                  lineHeight: 1.5,
                }}
              >
                {settlement
                  ? SETTLEMENT_COPY[settlement]
                  : 'PŁATNOŚĆ NIE ZOSTAŁA DOKOŃCZONA. ZAMÓWIENIE CZEKA - MOŻESZ ZAPŁACIĆ PONIŻEJ.'}
              </div>
            )}
            {!settlement && <StripePayButton order={order} token={token} />}
          </>
        )}

        <div
          style={{
            background: 'var(--shop-card-bg)',
            border: '2px solid rgba(var(--shop-muted-rgb),.15)',
            padding: 18,
          }}
        >
          {order ? (
            <>
              {order.items.map((item, i) => {
                const product = productById.get(item.productId);
                const colorLabel = product ? `${COLOR_META[product.color].label.toLowerCase()} · ` : '';
                return (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 14, fontSize: 13, marginBottom: 6 }}>
                    <span style={{ minWidth: 0 }}>
                      Koszulka · {colorLabel}
                      {item.cut} · {item.size}
                      {item.qty > 1 ? ` × ${item.qty}` : ''}
                      <ProductSpec product={product} fontSize={10.5} />
                    </span>
                    <span className="mono" style={{ flexShrink: 0 }}>{formatGroszeAsZl(item.qty * item.unitPriceGrosze)}</span>
                  </div>
                );
              })}
              {order.shippingMethod === 'zagranica' ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginTop: 4 }}>
                  <span>
                    Wysyłka zagraniczna
                    {order.shippingAddress.international?.countryName
                      ? ` · ${order.shippingAddress.international.countryName}`
                      : ''}
                  </span>
                  <span className="mono">USTALANA OSOBNO</span>
                </div>
              ) : (
                order.shippingMethod !== 'odbior' && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginTop: 4 }}>
                    <span>{order.shippingMethod === 'paczkomat' ? 'Paczkomat InPost' : 'Kurier InPost'}</span>
                    <span className="mono">{formatGroszeAsZl(order.shippingCostGrosze)}</span>
                  </div>
                )
              )}
              <div className="divider-dashed" style={{ margin: '14px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span className="display" style={{ fontSize: 18 }}>
                  RAZEM
                </span>
                <span className="display" style={{ fontSize: 26, color: 'var(--orange)' }}>
                  {formatGroszeAsZl(order.totalGrosze).toUpperCase()}
                </span>
              </div>
            </>
          ) : (
            <div style={{ fontSize: 13 }}>Ładuję szczegóły…</div>
          )}
        </div>

        <div>
          <div className="display" style={{ fontSize: 14, letterSpacing: '.12em', color: 'var(--orange)' }}>
            ↧ CO DALEJ
          </div>
          <ul style={{ marginTop: 8, paddingLeft: 18, fontSize: 13, lineHeight: 1.7 }}>
            {order?.email && (
              // A manual order's link may have been handed over by hand, so no email can be assumed.
              <li>
                {isPaid
                  ? 'Potwierdzenie wysłaliśmy na'
                  : order.isManual
                    ? 'Potwierdzenie płatności wyślemy na'
                    : 'Link do płatności wysłaliśmy na'}{' '}
                <b>{order.email.toUpperCase()}</b>.
              </li>
            )}
            {(!order || order.email) && (
              <li>Po nadaniu paczki dostaniesz e-mail z numerem przesyłki.</li>
            )}
            {order?.shippingMethod === 'zagranica' && (
              <li>
                Zapłaciłeś tylko za koszulki. Napiszemy do Ciebie, żeby ustalić sposób i koszt
                wysyłki
                {order.shippingAddress.international?.countryName
                  ? ` do ${order.shippingAddress.international.countryName}`
                  : ' za granicę'}
                .
              </li>
            )}
          </ul>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 'auto' }}>
          <button className="btn" style={{ flex: 1 }} onClick={() => navigate('/')}>
            ← WRÓĆ
          </button>
        </div>
      </div>
    </div>
  );
}
